import React, { useState, useRef, useEffect, useCallback } from "react";

export const MODULE_REPORT_TEMPLATES = [
  {
    id: 1,
    name: "Modern Corporate",
    desc: "Navy gradient hero · stat card · category tiles",
    themeId: "default",
  },
  {
    id: 2,
    name: "Clean Editorial",
    desc: "White page · teal accent stripe · editorial layout",
    themeId: "template-modern",
  },
  {
    id: 3,
    name: "Executive Dark",
    desc: "Charcoal cover · gold accents · premium look",
    themeId: "template-executive",
  },
  {
    id: 4,
    name: "Nature Minimal",
    desc: "Ivory canvas · green palette · organic cards",
    themeId: "template-nature",
  },
];

const THEME_TOKENS = {
  default: {
    heroBg: "linear-gradient(135deg, #102947 0%, #173d68 58%, #0f2f52 100%)",
    heroText: "#fff",
    accent: "#28a5ff",
    accentText: "#fff",
    bar: "#C9922A",
    cardBg: "#fff",
    cardBorder: "#c9d9ea",
    cardTopBorder: "#2c97f0",
    tableHeaderBg: "linear-gradient(135deg, #102947, #173d68)",
    tableHeaderText: "#fff",
    tableRowEven: "#f8fbff",
    tableCatBg: "#edf5fc",
    tableCatColor: "#173d68",
    footerBg: "#fff",
    footerBorder: "#9fb8d6",
    footerText: "#6c8bb0",
    pageBg: "#fff",
    statBorder: "rgba(42,160,255,0.45)",
    statBg: "rgba(41,111,176,0.20)",
    pillBg: "rgba(35,145,234,0.18)",
    pillColor: "#22a0ff",
  },
  "template-modern": {
    heroBg: "#fff",
    heroText: "#1e2d3d",
    accent: "#3f88a5",
    accentText: "#3f88a5",
    bar: "#3f88a5",
    cardBg: "#fff",
    cardBorder: "#dde8f2",
    cardTopBorder: "#3f88a5",
    tableHeaderBg: "#f0f7ff",
    tableHeaderText: "#1e2d3d",
    tableRowEven: "#fafcff",
    tableCatBg: "#f5f9fc",
    tableCatColor: "#3f88a5",
    footerBg: "#fff",
    footerBorder: "#3f88a5",
    footerText: "#8faab8",
    pageBg: "#fff",
    statBorder: "#3f88a5",
    statBg: "#fff",
    pillBg: "#e8f4fb",
    pillColor: "#3f88a5",
    heroBorderLeft: "8px solid #3f88a5",
  },
  "template-executive": {
    heroBg: "linear-gradient(160deg, #0d0d1e 0%, #1a1a2e 45%, #161626 100%)",
    heroText: "#e8e8ef",
    accent: "#c9a84c",
    accentText: "#c9a84c",
    bar: "#c9a84c",
    cardBg: "rgba(255,255,255,0.04)",
    cardBorder: "rgba(201,168,76,0.18)",
    cardTopBorder: "#c9a84c",
    tableHeaderBg: "rgba(201,168,76,0.07)",
    tableHeaderText: "#c9a84c",
    tableRowEven: "rgba(255,255,255,0.025)",
    tableCatBg: "rgba(201,168,76,0.04)",
    tableCatColor: "#c9a84c",
    footerBg: "#0d0d1e",
    footerBorder: "rgba(201,168,76,0.18)",
    footerText: "rgba(232,232,239,0.38)",
    pageBg: "#1a1a2e",
    statBorder: "rgba(201,168,76,0.32)",
    statBg: "rgba(255,255,255,0.04)",
    pillBg: "rgba(201,168,76,0.12)",
    pillColor: "#c9a84c",
  },
  "template-nature": {
    heroBg: "linear-gradient(135deg, #1a3a2a 0%, #2d5a3d 55%, #1e4530 100%)",
    heroText: "#fff",
    accent: "#86efac",
    accentText: "#86efac",
    bar: "#4ade80",
    cardBg: "#fff",
    cardBorder: "#d4e6d4",
    cardTopBorder: "#4ade80",
    tableHeaderBg: "#e8f5e9",
    tableHeaderText: "#1a3a2a",
    tableRowEven: "#fafdf8",
    tableCatBg: "#f0faf0",
    tableCatColor: "#2d5a3d",
    footerBg: "#1a3a2a",
    footerBorder: "rgba(134,239,172,0.35)",
    footerText: "rgba(255,255,255,0.42)",
    pageBg: "#f5f2ec",
    statBorder: "rgba(134,239,172,0.28)",
    statBg: "rgba(255,255,255,0.06)",
    pillBg: "rgba(134,239,172,0.13)",
    pillColor: "#86efac",
  },
};

function getPreviewRows(reportTitle) {
  const normalized = String(reportTitle || "").trim().toLowerCase();

  if (normalized.includes("waste")) {
    return {
      headers: ["Category", "Source", "Waste"],
      rows: [
        ["Bio-Medical Waste", "Blue", "1,78,067.38 Kg"],
        ["Hazardous Waste", "E-waste", "31,320.46 Kg"],
        ["Liquid Hazardous", "Effluent", "5,942.56 L"],
      ],
      total: "69,07,265.85",
      totalLabel: "5 Waste Categories",
    };
  }

  if (normalized.includes("scope")) {
    return {
      headers: ["Type", "Emission", "Unit"],
      rows: [
        ["Diesel", "127.88", "tCo2"],
        ["LPG", "222.51", "tCo2"],
        ["Petrol", "171.61", "tCo2"],
      ],
      total: "615.57",
      totalLabel: "4 Emission Types",
    };
  }

  if (normalized.includes("water")) {
    return {
      headers: ["Category", "Source", "Water"],
      rows: [
        ["Surface Water", "River Intake", "540 KL"],
        ["Ground Water", "Borewell", "920 KL"],
        ["Municipal", "Town Supply", "687 KL"],
      ],
      total: "2,847",
      totalLabel: "3 Water Categories",
    };
  }

  return {
    headers: ["Category", "Source", "Value"],
    rows: [
      ["Energy Con.", "Solar PV", "842 GJ"],
      ["Fuel Con.", "Diesel", "475 KL"],
      ["Electricity", "Grid", "1,320"],
    ],
    total: "2,847",
    totalLabel: "3 Types",
  };
}

function getPreviewTotalFontSize(total) {
  const digitsOnlyLength = String(total || "").replace(/[^0-9]/g, "").length;

  if (digitsOnlyLength >= 8) return 8.5;
  if (digitsOnlyLength >= 6) return 9.5;
  return 11;
}

function TemplatePreviewCard({ template, reportTitle = "Energy" }) {
  const tk = THEME_TOKENS[template.themeId] || THEME_TOKENS.default;
  const isClean = template.themeId === "template-modern";
  const isDark = template.themeId === "template-executive";
  const { headers, rows, total, totalLabel } = getPreviewRows(reportTitle);
  const totalFontSize = getPreviewTotalFontSize(total);

  return (
    <div style={{
      width: "100%",
      height: "100%",
      background: tk.pageBg,
      borderRadius: 6,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      fontFamily: isDark ? "'Times New Roman', serif" : "'Georgia', serif",
      fontSize: 10,
    }}>
      <div style={{
        background: tk.heroBg,
        borderLeft: isClean ? tk.heroBorderLeft : undefined,
        borderBottom: isClean ? "2px solid #e8edf2" : undefined,
        padding: "10px 11px 9px",
        color: tk.heroText,
        position: "relative",
        flexShrink: 0,
      }}>
        {!isClean && (
          <div style={{ height: 2, background: tk.bar, position: "absolute", top: 0, left: 0, right: 0 }} />
        )}
        <div style={{
          display: "inline-block",
          fontSize: 6,
          fontWeight: 700,
          letterSpacing: "1.2px",
          textTransform: "uppercase",
          color: tk.accent,
          background: isClean ? "#e8f4fb" : `${tk.accent}22`,
          padding: "2px 6px",
          borderRadius: isClean ? 2 : 99,
          marginBottom: 6,
          border: isClean ? "none" : `1px solid ${tk.accent}44`,
        }}>
          RIU.AI · ESG Platform
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: isClean ? "#0e1f30" : "#fff", lineHeight: 1.1, marginBottom: 3 }}>
              {reportTitle} <span style={{ color: tk.accent }}>Report</span>
            </div>
            <div style={{ fontSize: 7, opacity: .55, marginBottom: 6 }}>AI-Driven ESG & Sustainability</div>
            <div style={{ display: "flex", gap: 8 }}>
              {["Organisation", "FY"].map((lbl, i) => (
                <div key={lbl}>
                  <div style={{ fontSize: 6, opacity: .5, textTransform: "uppercase", letterSpacing: ".8px" }}>{lbl}</div>
                  <div style={{ fontSize: 7.5, fontWeight: 700 }}>{i === 0 ? "Yasil Tech" : "2024-25"}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{
            border: `1px solid ${tk.statBorder}`,
            borderRadius: isClean ? 3 : isDark ? 3 : 8,
            padding: "6px 8px",
            background: tk.statBg,
            textAlign: "center",
            minWidth: 68,
            maxWidth: 76,
            boxShadow: isClean ? "3px 3px 0 #d0e8f4" : undefined,
            overflow: "hidden",
          }}>
            <div style={{ fontSize: 6, color: isClean ? "#8faab8" : tk.accent, textTransform: "uppercase", letterSpacing: ".8px", fontWeight: 700 }}>Total</div>
            <div style={{
              fontSize: totalFontSize,
              fontWeight: 800,
              color: tk.accent,
              margin: "3px 0 2px",
              lineHeight: 1.05,
              whiteSpace: "nowrap",
            }}>{total}</div>
            <div style={{ fontSize: 5.5, opacity: .5 }}>FY 2024-25</div>
            <div style={{
              display: "inline-block", marginTop: 4, padding: "1px 5px",
              borderRadius: isClean ? 2 : 99,
              background: tk.pillBg, color: tk.pillColor, fontSize: 6, fontWeight: 700,
            }}>{totalLabel}</div>
          </div>
        </div>
      </div>

      <div style={{ height: 2, background: tk.bar, flexShrink: 0 }} />

      <div style={{
        display: "flex", gap: 4, padding: "5px 8px 4px",
        background: isClean ? "#f7f9fc" : isDark ? "#1a1a2e" : tk.pageBg,
        flexShrink: 0,
        borderBottom: isClean ? "1px solid #e8edf2" : undefined,
      }}>
        {["Cat A", "Cat B", "Cat C"].map((cat, i) => (
          <div key={cat} style={{
            flex: 1, padding: "4px 5px",
            background: tk.cardBg,
            border: `1px solid ${tk.cardBorder}`,
            borderTop: `2px solid ${[tk.cardTopBorder, "#e8963a", isDark ? "#e05353" : "#ef4444"][i]}`,
            borderRadius: isClean ? 3 : isDark ? 2 : 6,
          }}>
            <div style={{ fontSize: 5.5, textTransform: "uppercase", letterSpacing: ".5px", color: isDark ? "rgba(201,168,76,0.55)" : "#8faab8", marginBottom: 2 }}>{cat}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: [tk.cardTopBorder, "#e8963a", isDark ? "#e05353" : "#ef4444"][i] }}>1,240</div>
            <div style={{ fontSize: 5, opacity: .5 }}>units</div>
          </div>
        ))}
      </div>

      <div style={{ flex: 1, overflow: "hidden", padding: "4px 6px 0" }}>
        <div style={{
          padding: "3px 6px",
          background: tk.tableHeaderBg,
          color: tk.tableHeaderText,
          fontSize: 7,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: ".8px",
          marginBottom: 1,
          borderBottom: isClean ? `2px solid ${tk.accent}` : `1px solid ${tk.bar}44`,
        }}>
          {reportTitle} Breakdown
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1.5fr", gap: 1 }}>
          {headers.map((h) => (
            <div key={h} style={{
              padding: "2px 4px", fontSize: 6, fontWeight: 700,
              background: isDark ? "rgba(201,168,76,0.06)" : isClean ? "#f5f9fc" : "#f0faf0",
              color: tk.tableCatColor, textTransform: "uppercase", letterSpacing: ".4px",
              borderBottom: `1px solid ${tk.cardBorder}`,
            }}>{h}</div>
          ))}
        </div>
        {rows.map((row, ri) => (
          <div key={`${row[0]}-${ri}`} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1.5fr", gap: 1 }}>
            {row.map((cell, ci) => (
              <div key={`${cell}-${ci}`} style={{
                padding: "2px 4px", fontSize: 6,
                background: ri % 2 === 1 ? tk.tableRowEven : tk.cardBg,
                color: ci === 0 ? tk.tableCatColor : isDark ? "#e8e8ef" : "#1e2d3d",
                fontWeight: ci === 0 ? 700 : 400,
                borderBottom: `1px solid ${tk.cardBorder}44`,
              }}>{cell}</div>
            ))}
          </div>
        ))}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1.5fr", gap: 1 }}>
          {["Grand Total", "", total].map((cell, ci) => (
            <div key={`${cell}-${ci}`} style={{
              padding: "2px 4px", fontSize: 6, fontWeight: 700,
              background: isClean ? "#f0f7ff" : isDark ? "rgba(201,168,76,0.06)" : "#e8f5e9",
              color: tk.accentText,
            }}>{cell}</div>
          ))}
        </div>
      </div>

      <div style={{
        padding: "4px 8px",
        background: tk.footerBg,
        borderTop: `1px solid ${tk.footerBorder}`,
        color: tk.footerText,
        fontSize: 6,
        display: "flex",
        justifyContent: "space-between",
        flexShrink: 0,
        marginTop: "auto",
      }}>
        <span>Yasil Technology Pvt. Ltd. · Confidential</span>
        <span style={{ color: tk.accent, fontWeight: 700 }}>RIU.ai</span>
      </div>
    </div>
  );
}

export default function ModuleReportTemplateSelector({
  selectedTemplateId,
  onSelectTemplate,
  selectedReportTemplate,
  reportTemplateOptions,
}) {
  const initialIndex = Math.max(
    0,
    MODULE_REPORT_TEMPLATES.findIndex((t) => t.id === selectedTemplateId)
  );
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const containerRef = useRef(null);

  const reportTitle =
    reportTemplateOptions?.find((o) => o.value === selectedReportTemplate)?.label || "Energy";

  const total = MODULE_REPORT_TEMPLATES.length;

  useEffect(() => {
    const nextIndex = MODULE_REPORT_TEMPLATES.findIndex((t) => t.id === selectedTemplateId);
    if (nextIndex >= 0) {
      setActiveIndex(nextIndex);
    }
  }, [selectedTemplateId]);

  const navigate = useCallback((dir) => {
    setActiveIndex((prev) => (prev + dir + total) % total);
  }, [total]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft") navigate(-1);
      if (e.key === "ArrowRight") navigate(1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  const activeTemplate = MODULE_REPORT_TEMPLATES[activeIndex];

  const getSlot = (offset) =>
    MODULE_REPORT_TEMPLATES[(activeIndex + offset + total) % total];

  const slots = [
    { tpl: getSlot(-2), offset: -2 },
    { tpl: getSlot(-1), offset: -1 },
    { tpl: getSlot(0), offset: 0 },
    { tpl: getSlot(1), offset: 1 },
    { tpl: getSlot(2), offset: 2 },
  ];

  const selectActive = () => {
    onSelectTemplate(activeTemplate.id);
  };

  const isSelected = selectedTemplateId === activeTemplate.id;

  const slotStyle = (offset) => {
    const abs = Math.abs(offset);
    const scale = abs === 0 ? 1 : abs === 1 ? 0.76 : 0.58;
    const opacity = abs === 0 ? 1 : abs === 1 ? 0.55 : 0.25;
    const zIndex = abs === 0 ? 10 : abs === 1 ? 5 : 1;
    const shadow =
      abs === 0
        ? "0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.1)"
        : "0 4px 16px rgba(0,0,0,0.08)";
    const border =
      abs === 0
        ? isSelected
          ? "2.5px solid #3f88a5"
          : "2.5px solid #1a1a1a"
        : "2px solid transparent";
    const translateX = offset * 210;
    const translateY = abs === 0 ? -6 : abs === 1 ? 4 : 10;

    return {
      position: "absolute",
      width: 180,
      height: 260,
      top: "50%",
      left: "50%",
      transform: `translate(calc(-50% + ${translateX}px), calc(-50% + ${translateY}px)) scale(${scale})`,
      opacity,
      zIndex,
      boxShadow: shadow,
      border,
      borderRadius: 12,
      overflow: "hidden",
      cursor: abs === 0 ? "default" : "pointer",
      transition: "all 0.32s cubic-bezier(0.34,1.12,0.64,1)",
      background: "#fff",
    };
  };

  return (
    <div style={{ width: "100%", userSelect: "none" }}>
      <div style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "1.2px",
        textTransform: "uppercase",
        color: "#6b7280",
        marginBottom: 10,
        textAlign: "left",
      }}>
        Report Template
      </div>

      <div style={{
        position: "relative",
        width: "100%",
        height: 320,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }} ref={containerRef}>
        {slots.map(({ tpl, offset }) => (
          <div
            key={`${tpl.id}-${offset}`}
            style={slotStyle(offset)}
            onClick={() => {
              if (offset !== 0) {
                navigate(offset > 0 ? 1 : -1);
              }
            }}
          >
            <TemplatePreviewCard template={tpl} reportTitle={reportTitle} />
          </div>
        ))}

        <button
          onClick={() => navigate(-1)}
          style={{
            position: "absolute",
            left: 8,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 20,
            width: 38,
            height: 38,
            borderRadius: "50%",
            border: "1px solid #e2e8f0",
            background: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            color: "#374151",
            transition: "all .15s",
          }}
        >
          ‹
        </button>

        <button
          onClick={() => navigate(1)}
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 20,
            width: 38,
            height: 38,
            borderRadius: "50%",
            border: "1px solid #e2e8f0",
            background: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            color: "#374151",
            transition: "all .15s",
          }}
        >
          ›
        </button>
      </div>

      <div style={{
        textAlign: "center",
        marginTop: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
          {activeTemplate.name}
        </div>
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          {activeTemplate.desc}
        </div>

        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          {MODULE_REPORT_TEMPLATES.map((t, i) => (
            <button
              key={t.id}
              onClick={() => setActiveIndex(i)}
              style={{
                width: i === activeIndex ? 20 : 7,
                height: 7,
                borderRadius: 99,
                background: i === activeIndex ? "#3f88a5" : "#d1d5db",
                border: "none",
                cursor: "pointer",
                padding: 0,
                transition: "all .2s",
              }}
            />
          ))}
        </div>

        <button
          onClick={selectActive}
          style={{
            marginTop: 8,
            padding: "8px 24px",
            borderRadius: 6,
            border: "1.5px solid #3f88a5",
            background: isSelected ? "#3f88a5" : "#fff",
            color: isSelected ? "#fff" : "#3f88a5",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            transition: "all .15s",
          }}
        >
          {isSelected ? "✓ Selected" : "Use This Template"}
        </button>
      </div>
    </div>
  );
}
